import { Button } from '@heroui/react'
import { FormikControl } from '@qse/ui'
import type { FormikHelpers } from 'formik'
import { Form, Formik } from 'formik'
import { ArrowLeft } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { useForgotPassword } from '../logic'

interface NewPasswordData {
  newPassword: string
  confirmPassword: string
}

const ResetPasswordScreen: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const { handleSetNewPassword, newPasswordValidationSchema } = useForgotPassword()

  const email = location.state?.email
  const confirmationCode = location.state?.confirmationCode

  if (!email || !confirmationCode) {
    navigate('/auth/forgot-password', { replace: true })

    return null
  }

  const handleSubmit = async (
    values: NewPasswordData,
    formikHelpers: FormikHelpers<NewPasswordData>,
  ) => {
    try {
      formikHelpers.setStatus('')

      await handleSetNewPassword(email, confirmationCode, values, formikHelpers)
    } catch (error: any) {
      console.error('Password reset error:', error)
    }
  }

  return (
    <div className="h-full">
      <div className="sticky top-0 flex lg:justify-end justify-between z-50 w-full px-4 py-4 dark:bg-[#000] bg-white">
        <Button
          as={Link}
          className="font-medium"
          startContent={<ArrowLeft size={16} />}
          state={{ email }}
          to="/auth/verification"
          variant="light"
        >
          Back
        </Button>
      </div>

      <div className="flex items-center h-[calc(100vh-72px)] w-full lg:pl-[110px] lg:max-w-full max-w-[440px] mx-auto">
        <div className="lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2">
            <h1 className="text-[28px] font-medium">Please Type New Password</h1>
            <p className="text-[15px]">We sent a code to your email.</p>
          </div>

          <Formik
            validationSchema={newPasswordValidationSchema}
            initialValues={{
              newPassword: '',
              confirmPassword: '',
            }}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <div className="flex flex-col gap-[30px]">
                  <FormikControl
                    control="password"
                    label="Password"
                    name="newPassword"
                    placeholder="Enter new password"
                    radius="sm"
                    size="md"
                    variant="bordered"
                  />

                  <FormikControl
                    control="password"
                    label="Re-type Password"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    radius="sm"
                    size="md"
                    variant="bordered"
                  />
                </div>

                <Button
                  className="w-full mt-6 font-medium"
                  color="primary"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  radius="sm"
                  type="submit"
                >
                  Create new password
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordScreen
